import React from "react";
import { Link } from "react-router-dom";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import DescriptionIcon from "@mui/icons-material/Description";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { Candidate } from "../../utils/CandidateDashboard";
import {
  formatLeadPhone,
  getLeadInitials,
  leadStatusLabel,
} from "../../utils/campaignLeadHelpers";

interface CampaignLeadCardProps {
  lead: Candidate;
  onCallToAction?: (id: number) => void;
  showCta?: boolean;
  compact?: boolean;
  inDrawer?: boolean;
}

const CampaignLeadCard: React.FC<CampaignLeadCardProps> = ({
  lead,
  onCallToAction,
  showCta = true,
  compact = false,
  inDrawer = false,
}) => {
  const fullName =
    `${lead?.firstName || ""} ${lead?.lastName || ""}`.trim() || "N/A";

  if (inDrawer) {
    return (
      <div className="campaign-lead-card--drawer">
        <div className="campaign-lead-card--drawer-avatar">
          {lead?.coverLetter ? (
            <img src={lead.coverLetter} alt={fullName} />
          ) : (
            getLeadInitials(lead?.firstName, lead?.lastName)
          )}
        </div>

        <div className="campaign-lead-card--drawer-main">
          <div className="campaign-lead-card--drawer-top">
            <div className="campaign-lead-card--drawer-identity">
              <p className="campaign-lead-card--drawer-name">{fullName}</p>
              <span className="campaign-lead-card--drawer-role">
                {lead?.jobTitle || "—"}
              </span>
            </div>
            <span className="campaign-lead-card--drawer-status">
              {leadStatusLabel(lead?.status)}
            </span>
          </div>

          <div className="campaign-lead-card--drawer-meta">
            <span className="campaign-lead-card--drawer-meta-item">
              <EmailIcon sx={{ fontSize: 13 }} />
              <Link to={`mailto:${lead?.email}`}>{lead?.email || "N/A"}</Link>
            </span>
            <span className="campaign-lead-card--drawer-meta-divider" />
            <span className="campaign-lead-card--drawer-meta-item">
              <PhoneIcon sx={{ fontSize: 13 }} />
              {formatLeadPhone(lead?.countryCode, lead?.phone)}
            </span>
            {lead?.cv && (
              <>
                <span className="campaign-lead-card--drawer-meta-divider" />
                <span className="campaign-lead-card--drawer-meta-item campaign-lead-card--drawer-meta-item--cv">
                  <DescriptionIcon sx={{ fontSize: 13 }} />
                  <Link to={lead.cv} target="_blank">
                    CV
                  </Link>
                </span>
              </>
            )}
          </div>
        </div>

        {showCta && onCallToAction && (
          <button
            type="button"
            className="campaign-lead-card--drawer-action"
            onClick={() => onCallToAction(lead.id)}
            aria-label={`View ${fullName}`}
            title="View candidate"
          >
            <ArrowForwardRoundedIcon sx={{ fontSize: 17 }} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      className={`listing-card campaign-lead-card${
        compact ? " campaign-lead-card--compact" : ""
      }`}
    >
      <div className="campaign-lead-card-body">
        <div className="campaign-lead-card-info">
          <div className="listing-card-title-row">
            <div className="listing-avatar">
              {lead?.coverLetter ? (
                <img src={lead.coverLetter} alt="Profile" />
              ) : (
                getLeadInitials(lead?.firstName, lead?.lastName)
              )}
            </div>
            <div>
              <p className="listing-card-title">{fullName}</p>
              <div className="listing-card-subtitle">{lead?.jobTitle || "—"}</div>
              <span
                className="listing-status-chip listing-status-chip--active"
                style={{ marginTop: 8 }}
              >
                {leadStatusLabel(lead?.status)}
              </span>
            </div>
          </div>

          <div className="listing-meta-row">
            <span className="listing-meta-chip">
              <PhoneIcon />
              {formatLeadPhone(lead?.countryCode, lead?.phone)}
            </span>
            <span className="listing-meta-chip">
              <EmailIcon />
              <Link
                to={`mailto:${lead?.email}`}
                style={{ color: "inherit", textDecoration: "none" }}
              >
                {lead?.email || "N/A"}
              </Link>
            </span>
            {lead?.cv && (
              <span className="listing-meta-chip">
                <DescriptionIcon />
                <Link
                  to={lead.cv}
                  target="_blank"
                  style={{
                    color: "#56c6a9",
                    textDecoration: "none",
                    fontWeight: 600,
                  }}
                >
                  View CV
                </Link>
              </span>
            )}
          </div>
        </div>

        {showCta && onCallToAction && (
          <button
            type="button"
            className="listing-cta-btn"
            onClick={() => onCallToAction(lead.id)}
          >
            Call to Action
          </button>
        )}
      </div>
    </div>
  );
};

export default CampaignLeadCard;
